import { Box, Spinner, StatusIndicator } from "@amzn/awsui-components-react";
import * as React from "react";
import { PreviewRendererProps } from "./PdfPreviewRenderer";

/** DOMPurify allowlist for mammoth-generated HTML (tables, images only) */
const DOCX_SANITIZE_CONFIG = {
    USE_PROFILES: { html: true },
    ADD_TAGS: ["table", "thead", "tbody", "tr", "th", "td", "img"],
    ADD_ATTR: ["src", "alt", "colspan", "rowspan"],
    ALLOW_DATA_ATTR: false,
};

/** Container style for the rendered HTML preview */
const PREVIEW_CONTAINER_STYLE: React.CSSProperties = {
    padding: "24px",
    maxHeight: "80vh",
    overflowY: "auto",
};

/**
 * DOCX renderer — fetches the document via presigned URL, converts to HTML
 * using mammoth (dynamic import), and sanitizes with DOMPurify before render.
 *
 * Both libraries are loaded on-demand to keep them out of the main bundle (~230KB).
 * The rendered HTML is sanitized to prevent XSS from malicious DOCX content.
 */
export const DocxPreviewRenderer: React.FC<PreviewRendererProps> = ({ url }) => {
    const [html, setHtml] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const abortRef = React.useRef<AbortController | null>(null);

    React.useEffect(() => {
        const controller = new AbortController();
        abortRef.current = controller;

        (async () => {
            try {
                const [mammoth, DOMPurifyModule] = await Promise.all([
                    import("mammoth"),
                    import("dompurify")
                ]);
                // dompurify uses `export =` (CJS) which webpack's dynamic import interop wraps
                // inconsistently. This cast is required because esModuleInterop is disabled in
                // this tsconfig. The typeof target gives full IntelliSense on .sanitize().
                // TODO(PF-344): Remove when esModuleInterop is enabled project-wide.
                const DOMPurify = DOMPurifyModule as unknown as typeof import("dompurify");

                // Guard: DOMPurify returns empty string if DOM is unavailable (SSR, jsdom)
                if (!DOMPurify.isSupported) {
                    setError("Document preview is not supported in this browser.");
                    return;
                }

                const response = await fetch(url, { signal: controller.signal });
                if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

                const arrayBuffer = await response.arrayBuffer();
                const result = await mammoth.convertToHtml({ arrayBuffer });
                const sanitized = DOMPurify.sanitize(result.value, DOCX_SANITIZE_CONFIG);
                setHtml(sanitized);
            } catch (e) {
                if (e instanceof Error && e.name === "AbortError") return;
                console.error("[DocxPreviewRenderer] Conversion failed", e);
                setError("Failed to render document preview. Please try downloading the file.");
            } finally {
                setLoading(false);
            }
        })();

        return () => { controller.abort(); };
    }, [url]);

    if (loading) {
        return <div style={{ textAlign: "center", padding: "40px" }}>
            <Spinner size="large" />
            <Box margin={{ top: "s" }}>Converting document...</Box>
        </div>;
    }
    if (error) return <StatusIndicator type="error">{error}</StatusIndicator>;
    if (html) {
        return <div
            dir="auto"
            style={PREVIEW_CONTAINER_STYLE}
            dangerouslySetInnerHTML={{ __html: html }}
        />;
    }
    return null;
};
